import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import { useProjectStore } from '../store/projectStore';
import {
  useCurrentWorkspace,
  useWorkspaceStore,
} from '../store/workspaceStore';
import { WORKSPACE_WINDOW_KIND } from '../electron-shared/workspace/workspace-descriptor';
import { useTargets } from './useTargets';
import { useAutoSelectCompany } from './useAutoSelectCompany';
import { useUserTenants } from './api/useUserTenants';

export function useWorkspaceTargetSync(): void {
  const workspace = useCurrentWorkspace();
  const setResolved = useWorkspaceStore((state) => state.setResolved);
  const { companyId, selectedTarget, setTarget } = useProjectStore(
    useShallow((state) => ({
      companyId: state.companyId,
      selectedTarget: state.selectedTarget,
      setTarget: state.setTarget,
    })),
  );
  const { isCompanyReady } = useAutoSelectCompany();
  const { targets, isFetched } = useTargets({ companyId });
  const { tenants } = useUserTenants();
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    const workspaceApi = window.electronAPI?.workspace;

    if (!workspace || !workspaceApi || !isCompanyReady || !isFetched) {
      return;
    }

    if (!hasRestoredRef.current) {
      hasRestoredRef.current = true;

      const savedTarget = targets.find(
        (target) =>
          target.id === workspace.targetId &&
          target.targetType === workspace.targetType,
      );
      const isShowingSavedTarget =
        selectedTarget?.id === workspace.targetId &&
        selectedTarget.targetType === workspace.targetType;

      if (savedTarget && !isShowingSavedTarget) {
        setTarget(savedTarget);

        return;
      }
    }

    const targetTenantId = selectedTarget?.tenantId ?? workspace.tenantId;

    if (!selectedTarget || targetTenantId !== workspace.tenantId) {
      return;
    }

    const tenantName =
      tenants.find((tenant) => tenant.id === workspace.tenantId)?.name ??
      workspace.tenantName;
    const isRecorded =
      selectedTarget.id === workspace.targetId &&
      selectedTarget.targetType === workspace.targetType &&
      selectedTarget.name === workspace.targetName &&
      tenantName === workspace.tenantName;

    if (isRecorded) {
      return;
    }

    void workspaceApi
      .syncTarget({
        tenantId: workspace.tenantId,
        tenantName,
        targetId: selectedTarget.id,
        targetType: selectedTarget.targetType,
        targetName: selectedTarget.name,
      })
      .then((result) => {
        if (result.success) {
          setResolved({
            kind: WORKSPACE_WINDOW_KIND.WORKSPACE,
            workspace: result.data,
          });
        }
      })
      .catch((error: unknown) => {
        console.warn(
          '[useWorkspaceTargetSync] Failed to record the window target; the tray and recents may show the previous application',
          { error: error instanceof Error ? error : new Error(String(error)) },
        );
      });
  }, [
    isCompanyReady,
    isFetched,
    selectedTarget,
    setResolved,
    setTarget,
    targets,
    tenants,
    workspace,
  ]);
}
